/**
 * The control that lets somebody holding more than one team move between
 * them.
 *
 * Purely presentational: `AppShell` calls `useTeams()` once and hands this
 * component the result, rather than each having its own copy of the fetch.
 * Two independent calls to `useTeams()` inside one shell would mean two
 * requests to `/api/team/memberships` on every mount, for data that does not
 * differ between them.
 *
 * Renders nothing for zero or one membership: a switcher offering one option
 * is a control that does nothing, and it is not rendered while the list is
 * still loading either, so the header does not pop a button into existence a
 * moment after first paint only to potentially remove it again once the
 * answer turns out to be "one team". A signed-in person always holds at least
 * one membership (the one they are using right now), so the zero case is
 * defensive rather than expected.
 */

import { useState, type ReactNode } from 'react';
import { Dialog } from '@good-season/ui';

import type { TeamMembership } from '../team/useTeams.js';

export interface TeamSwitcherProps {
  readonly memberships: readonly TeamMembership[];
  /**
   * The team already showing, for marking the matching row.
   *
   * Optional: `Landing` renders the shell before it knows a team at all (see
   * its own header comment on why it holds no team state of its own), so this
   * can be absent. The list still opens and still switches; it just cannot
   * say which row is the one already on screen.
   */
  readonly currentTeamId?: string | undefined;
  readonly onSelect: (teamId: string) => void;
}

export function TeamSwitcher({
  memberships,
  currentTeamId,
  onSelect,
}: TeamSwitcherProps): ReactNode {
  const [open, setOpen] = useState(false);

  /*
   * TEAMS, deduplicated, not memberships.
   *
   * `/api/team/memberships` returns one entry per (team, season) pair, and
   * `membershipsFor` says so: the unique key is `(user_id, team_id, season_id)`
   * so one person holds two accepted memberships on the same team across two
   * seasons, which BUILD_PLAN 4.2 makes the normal shape because a team has a
   * persistent identity and rolling over copies the roster.
   *
   * Listing them raw gave a returning coach of ONE team two rows, both marked
   * Current, sharing a React key, and neither able to do anything: `?teamId=`
   * names a team and never a season, so the season label was a choice the
   * mechanism cannot honour. Found by the fresh-context pass.
   *
   * The season is dropped from the row for the same reason. A label you cannot
   * act on is worse than no label.
   */
  const teams = memberships.filter(
    (entry, at) => memberships.findIndex((other) => other.teamId === entry.teamId) === at,
  );

  /*
   * A selection that is not among the teams held is STALE, and it has to be
   * escapable.
   *
   * This is the lockout the fresh-context pass graded S1. Every request carries
   * the selection, the server refuses a team the person does not hold, and with
   * one team held the control did not render at all: no way to clear it, every
   * screen refused, and the only escape was signing out. A shared link or a team
   * that was since soft-deleted both reach it.
   *
   * So the control renders whenever there is somewhere else to go OR the
   * current selection is not somewhere they can be.
   */
  const stale = currentTeamId !== undefined && !teams.some((t) => t.teamId === currentTeamId);
  if (teams.length <= 1 && !stale) return null;

  function choose(teamId: string): void {
    onSelect(teamId);
    setOpen(false);
  }

  return (
    <>
      <button
        type="button"
        className="shell__switcher"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
      >
        {stale ? 'Choose a team' : 'Switch team'}
      </button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={stale ? 'Choose a team' : 'Switch team'}
        description={
          stale
            ? 'The team in this link is not one you are on. Pick one of yours to carry on.'
            : 'The schedule, the roster and everything else switch to whichever team you pick.'
        }
      >
        <ul className="teamSwitcher__list">
          {teams.map((membership) => {
            const isCurrent = membership.teamId === currentTeamId;
            return (
              <li key={membership.teamId}>
                <button
                  type="button"
                  className="teamSwitcher__option"
                  onClick={() => choose(membership.teamId)}
                  aria-current={isCurrent ? 'true' : undefined}
                >
                  <span className="teamSwitcher__optionText">
                    <span className="teamSwitcher__optionName">{membership.teamName}</span>
                  </span>
                  {isCurrent ? <span className="teamSwitcher__current">Current</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      </Dialog>
    </>
  );
}
